import { useState, useEffect } from 'react';
import { X, CalendarCheck, ChevronDown, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import { toast } from 'sonner';
import { useRegularizeAttendance } from '@/api/queries/attendance.queries';
import { useSubmitLeave, useLeaveBalances } from '@/api/queries/leave.queries';

// Per-employee leave balance row (hook must be called inside component, not conditionally)
function EmployeeLeaveRow({ emp, leaveType }) {
  const { data } = useLeaveBalances(emp.id);
  const balances = data?.data || [];
  const bal = balances.find(b => b.leave_code === leaveType);
  const available = bal?.available ?? '—';

  return (
    <div className="flex items-center gap-3 py-2 border-b border-[var(--border)] last:border-0">
      <div className="w-8 h-8 rounded-full bg-[var(--accent)]/15 flex items-center justify-center text-[12px] font-bold text-[var(--accent)] shrink-0">
        {(emp.first_name?.[0] || emp.name?.[0] || '?').toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium text-[var(--text-primary)] truncate">
          {emp.first_name ? `${emp.first_name} ${emp.last_name || ''}` : emp.name || emp.id}
        </p>
        <p className="text-[11px] text-[var(--text-tertiary)] truncate">
          {emp.employee_code || ''}{emp.department?.name ? ` · ${emp.department.name}` : ''}
        </p>
      </div>
      {leaveType && (
        <div className={cn(
          'text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0',
          typeof available === 'number' && available === 0
            ? 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400'
            : 'bg-[var(--bg-muted)] text-[var(--text-secondary)]'
        )}>
          {typeof available === 'number' ? `${available}d left` : '—'}
        </div>
      )}
    </div>
  );
}

// Leave balance display per leave type for a single employee (used to check exhaustion)
function useAllBalances(employees) {
  // We can't call hooks conditionally, so we pre-render a fixed max (10) of employees
  const e0 = useLeaveBalances(employees[0]?.id);
  const e1 = useLeaveBalances(employees[1]?.id);
  const e2 = useLeaveBalances(employees[2]?.id);
  const e3 = useLeaveBalances(employees[3]?.id);
  const e4 = useLeaveBalances(employees[4]?.id);
  const e5 = useLeaveBalances(employees[5]?.id);
  const e6 = useLeaveBalances(employees[6]?.id);
  const e7 = useLeaveBalances(employees[7]?.id);
  const e8 = useLeaveBalances(employees[8]?.id);
  const e9 = useLeaveBalances(employees[9]?.id);

  return [e0, e1, e2, e3, e4, e5, e6, e7, e8, e9].slice(0, employees.length).map((q, i) => ({
    employee_id: employees[i]?.id,
    balances: q?.data?.data || [],
  }));
}

const STATUS_OPTIONS = [
  { value: 'PRESENT',  label: 'Present',   color: 'text-emerald-600' },
  { value: 'ABSENT',   label: 'Absent',    color: 'text-red-500' },
  { value: 'LEAVE',    label: 'Leave',     color: 'text-amber-500' },
  { value: 'HALF_DAY', label: 'Half Day',  color: 'text-purple-500' },
  { value: 'LATE',     label: 'Late',      color: 'text-orange-500' },
];

const LEAVE_TYPES = [
  { key: 'EL', label: 'Earned Leave' },
  { key: 'CL', label: 'Casual Leave' },
  { key: 'SL', label: 'Sick Leave' },
];

export function BulkAttendanceModal({ open, onClose, employees }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [status, setStatus] = useState('PRESENT');
  const [leaveType, setLeaveType] = useState('EL');
  const [progress, setProgress] = useState(null); // null | { done, total }

  const regularize = useRegularizeAttendance();
  const submitLeave = useSubmitLeave();

  // Cap at 10 employees for hook safety (bulk is usually small)
  const cappedEmps = employees.slice(0, 10);
  const allBalances = useAllBalances(cappedEmps);

  // Reset when opened
  useEffect(() => {
    if (open) {
      setDate(today);
      setStatus('PRESENT');
      setLeaveType('EL');
      setProgress(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const isLeave = status === 'LEAVE';

  // Check if a leave type is exhausted for ALL selected employees
  const getLeaveAvailability = (code) => {
    if (!isLeave) return {};
    const out = {};
    cappedEmps.forEach((emp, i) => {
      const balRow = allBalances[i];
      if (!balRow) return;
      const b = balRow.balances.find(x => x.leave_code === code);
      out[emp.id] = b?.available ?? null;
    });
    return out;
  };

  const ltAvailability = getLeaveAvailability(leaveType);
  const someExhausted = Object.values(ltAvailability).some(v => v === 0);

  const handleSubmit = async () => {
    const total = employees.length;
    setProgress({ done: 0, total });

    let done = 0;
    let errors = 0;

    for (const emp of employees) {
      try {
        const name = emp.first_name ? `${emp.first_name} ${emp.last_name || ''}` : emp.name || '';
        const dept = emp.department?.name || emp.department || '';
        const desig = emp.designation?.name || emp.designation || '';

        await regularize.mutateAsync({
          employee_id: emp.id,
          employee_code: emp.employee_code || '',
          employee_name: name,
          department: dept,
          designation: desig,
          date,
          status: isLeave ? 'ON_LEAVE' : status,
          check_in: null,
          check_out: null,
          leave_type: isLeave ? leaveType : null,
        });

        if (isLeave) {
          await submitLeave.mutateAsync({
            employee_id: emp.id,
            employee_name: name,
            employee_code: emp.employee_code || '',
            department: dept,
            leave_type: leaveType === 'EL' ? 'EARNED_LEAVE' : leaveType === 'CL' ? 'CASUAL_LEAVE' : 'SICK_LEAVE',
            from_date: date,
            to_date: date,
            days: 1,
            reason: 'Marked via bulk attendance',
            source: 'HR_MARKED',
          }).catch(() => { /* non-fatal */ });
        }
      } catch {
        errors++;
      }
      done++;
      setProgress({ done, total });
    }

    if (errors === 0) {
      toast.success(`Attendance marked for ${total} employee${total !== 1 ? 's' : ''}`);
    } else {
      toast.warning(`${done - errors}/${total} employees marked (${errors} failed)`);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="w-full max-w-[480px] bg-[var(--bg-card)] rounded-[16px] border border-[var(--border)] shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-[var(--border)] shrink-0">
          <div>
            <h3 className="text-[16px] font-bold text-[var(--text-primary)]">Mark Attendance</h3>
            <p className="text-[12px] text-[var(--text-secondary)] mt-0.5">{employees.length} employee{employees.length !== 1 ? 's' : ''} selected</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-[8px] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)]">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Date */}
          <div>
            <label className="block text-[12px] font-semibold text-[var(--text-secondary)] mb-1.5">Date</label>
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              max={today}
              className="w-full px-3 py-2 rounded-[10px] border border-[var(--border)] bg-[var(--bg-muted)] text-[13px] text-[var(--text-primary)] outline-none focus:border-[var(--accent)]"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-[12px] font-semibold text-[var(--text-secondary)] mb-1.5">Status</label>
            <div className="grid grid-cols-5 gap-2">
              {STATUS_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setStatus(opt.value)}
                  className={cn(
                    'flex flex-col items-center py-2.5 px-1 rounded-[10px] border-2 transition-all text-center',
                    status === opt.value
                      ? 'border-[var(--accent)] bg-[var(--accent)]/5'
                      : 'border-[var(--border)] bg-[var(--bg-muted)] hover:border-[var(--accent)]/50',
                  )}
                >
                  <CalendarCheck size={14} className={cn('mb-1', status === opt.value ? 'text-[var(--accent)]' : opt.color)} />
                  <span className={cn('text-[10px] font-semibold leading-tight', status === opt.value ? 'text-[var(--accent)]' : 'text-[var(--text-primary)]')}>
                    {opt.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Leave type picker */}
          {isLeave && (
            <div>
              <label className="block text-[12px] font-semibold text-[var(--text-secondary)] mb-1.5">Leave Type</label>
              <div className="grid grid-cols-3 gap-2">
                {LEAVE_TYPES.map(lt => {
                  const sel = leaveType === lt.key;
                  return (
                    <button
                      key={lt.key}
                      type="button"
                      onClick={() => setLeaveType(lt.key)}
                      className={cn(
                        'flex flex-col items-center py-3 px-1 rounded-[12px] border-2 transition-all',
                        sel ? 'border-[var(--accent)] bg-[var(--accent)]/5' : 'border-[var(--border)] bg-[var(--bg-muted)] hover:border-[var(--accent)]/50',
                      )}
                    >
                      <span className={cn('text-[16px] font-black', sel ? 'text-[var(--accent)]' : 'text-[var(--text-primary)]')}>{lt.key}</span>
                      <span className="text-[9px] text-[var(--text-secondary)] text-center leading-tight mt-1">{lt.label}</span>
                    </button>
                  );
                })}
              </div>
              {someExhausted && (
                <div className="mt-2 flex items-center gap-1.5 text-[11px] text-amber-600 dark:text-amber-400">
                  <AlertCircle size={12} />
                  Some employees have 0 {leaveType} days remaining
                </div>
              )}
            </div>
          )}

          {/* Employee list with balances */}
          <div>
            <label className="block text-[12px] font-semibold text-[var(--text-secondary)] mb-1.5">
              Employees {isLeave ? `(${leaveType} balance)` : ''}
            </label>
            <div className="rounded-[10px] border border-[var(--border)] bg-[var(--bg-muted)] px-3 py-1 max-h-[180px] overflow-y-auto">
              {cappedEmps.map(emp => (
                <EmployeeLeaveRow
                  key={emp.id}
                  emp={emp}
                  leaveType={isLeave ? leaveType : null}
                />
              ))}
              {employees.length > 10 && (
                <p className="text-[11px] text-[var(--text-tertiary)] py-2 text-center">
                  +{employees.length - 10} more employees
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 pt-3 border-t border-[var(--border)] shrink-0">
          {progress ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-[13px] text-[var(--text-secondary)]">
                <Loader2 size={14} className="animate-spin" />
                Processing {progress.done}/{progress.total}...
              </div>
              <div className="h-1.5 rounded-full bg-[var(--bg-muted)] overflow-hidden">
                <div
                  className="h-full bg-[var(--accent)] transition-all"
                  style={{ width: `${(progress.done / progress.total) * 100}%` }}
                />
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <button onClick={onClose} className="flex-1 py-2.5 rounded-[10px] border border-[var(--border)] text-[13px] font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] transition-colors">
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="flex-1 py-2.5 rounded-[10px] bg-[var(--accent)] text-white text-[13px] font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              >
                <CheckCircle2 size={14} />
                Mark {status === 'LEAVE' ? `${leaveType} Leave` : STATUS_OPTIONS.find(s => s.value === status)?.label}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BulkAttendanceModal;
